const PERMISSION = 'geolocationPermission';
const codes = ['PERMISSION_DENIED', 'POSITION_UNAVAILABLE', 'TIMEOUT'];
const allowedCodes = ['granted', 'prompt', 'denied'];

/**
 * Method: getPermissionStatus
 * Description: Should return a string value of "granted", "prompt", or "denied"
 * Accepts: undefined
 * Returns: string
 */
function getPermissionStatus(): string {
  const permission = localStorage.getItem(PERMISSION);
  return permission || 'prompt';
}

/**
 * Method: setPermissionStatus
 * Description: Should accept a string value of "granted", "prompt", or "denied"
 * Accepts: @param status string from allowedCodes
 * Returns: void
 */
function setPermissionStatus(status: string): void {
  if (!allowedCodes.includes(status)) {
    status = 'prompt';
  }

  return localStorage.setItem(PERMISSION, status);
}

/*
 * Method: checkLocationPermission
 * Description: Should return a promise with a string with value of "granted", "prompt", or "denied"
 * Accepts: undefined
 * Returns: Promise<string>
 * Notes: https://developer.mozilla.org/en-US/docs/Web/API/Permissions/query
 */
export async function checkLocationPermission(): Promise<string> {
  if (!window.navigator.permissions || window.navigator.permissions.query) {
    const permissionStatus = getPermissionStatus();
    return permissionStatus || 'prompt';
  }

  try {
    const result = await window.navigator.permissions.query({ name: 'geolocation' });

    setPermissionStatus(result.state);
    return result.state;
  } catch {
    return 'prompt';
  }
}

/**
 * Method: getCurrentPosition
 * Description: Gets the current position of the device
 * Accepts:
 *   @param timeout time in ms before timing the request out - default 2500ms
 * Returns: Geolocation
 * Notes: https://developer.mozilla.org/en-US/docs/Web/API/GeolocationPositionError
 */
export async function getCurrentPosition(timeout = 5000): Promise<GeolocationPosition> {
  const permission = await checkLocationPermission();
  if (permission === 'denied') {
    return Promise.reject(
      Object.assign(new Error('Geolocation unauthenticated Failure'), {
        name: 'GeolocationDenied',
        status: codes[0],
        code: 1
      })
    );
  }

  return new Promise((resolve, reject) => {
    window.navigator.geolocation.getCurrentPosition(
      result => {
        const permissionStatus = getPermissionStatus();
        if (!permissionStatus || permissionStatus === 'prompt') {
          // Handle browsers without navigator.permissions
          setPermissionStatus('granted');
        }
        resolve(result);
      },
      ({ code, message }) => {
        if (code !== 1) {
          setPermissionStatus('prompt');
        }

        return reject(
          Object.assign(new Error(message), {
            name: 'PositionError',
            code,
            status: codes[code - 1]
          })
        );
      },
      { timeout }
    );
  });
}

const GeolocationServices = {
  getCurrentPosition,
  checkLocationPermission
};

export default GeolocationServices;
